import { createFileRoute } from '@tanstack/react-router';
import { useTheme } from '../../../contexts/ThemeContext';

/**
 * Creators Analytics - View sales and engagement metrics
 */
export const Route = createFileRoute('/_authenticated/creators/analytics')({
  component: CreatorsAnalytics,
});

function CreatorsAnalytics() {
  const { theme } = useTheme();

  const colors = {
    dark: {
      text: '#ffffff',
      textMuted: '#9ca3af',
      cardBg: 'linear-gradient(135deg, rgba(26, 26, 46, 0.6) 0%, rgba(22, 33, 62, 0.6) 100%)',
      cardBorder: 'rgba(102, 126, 234, 0.2)',
      buttonBg: '#667eea',
      buttonInactive: 'rgba(102, 126, 234, 0.2)',
      buttonInactiveText: '#a8b2ff',
      chartBg: 'rgba(15, 15, 20, 0.6)'
    },
    light: {
      text: '#111827',
      textMuted: '#6b7280',
      cardBg: '#ffffff',
      cardBorder: '#e5e7eb',
      buttonBg: '#2563eb',
      buttonInactive: '#f3f4f6',
      buttonInactiveText: '#374151',
      chartBg: '#f9fafb'
    }
  };

  const c = colors[theme];

  return (
    <div style={{ padding: '2rem' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '2rem' }}>
          <h1 style={{
            fontSize: '2rem',
            fontWeight: 'bold',
            color: c.text,
            marginBottom: '0.5rem'
          }}>
            Analytics
          </h1>
          <p style={{ color: c.textMuted, fontSize: '1rem' }}>
            Track your sales performance and customer engagement
          </p>
        </div>

        {/* Time Period Selector */}
        <div style={{
          display: 'flex',
          gap: '0.5rem',
          marginBottom: '2rem'
        }}>
          {['7 days', '30 days', '90 days', 'All time'].map((period) => (
            <button
              key={period}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: period === '30 days' ? c.buttonBg : c.buttonInactive,
                color: period === '30 days' ? 'white' : c.buttonInactiveText,
                border: 'none',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}
            >
              {period}
            </button>
          ))}
        </div>

        {/* Metrics Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '1.5rem',
          marginBottom: '2rem'
        }}>
          <MetricCard
            title="Total Revenue"
            value="$0.00"
            change="+0%"
            trend="neutral"
            theme={theme}
          />
          <MetricCard
            title="Sales"
            value="0"
            change="+0"
            trend="neutral"
            theme={theme}
          />
          <MetricCard
            title="Page Views"
            value="0"
            change="+0%"
            trend="neutral"
            theme={theme}
          />
          <MetricCard
            title="Conversion Rate"
            value="0%"
            change="+0%"
            trend="neutral"
            theme={theme}
          />
        </div>

        {/* Charts Placeholder */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
          gap: '1.5rem'
        }}>
          <div style={{
            background: c.cardBg,
            border: `1px solid ${c.cardBorder}`,
            borderRadius: '0.5rem',
            padding: '1.5rem',
            backdropFilter: theme === 'dark' ? 'blur(10px)' : 'none',
            boxShadow: theme === 'dark' ? '0 4px 6px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{
              fontSize: '1.125rem',
              fontWeight: '600',
              color: c.text,
              marginBottom: '1rem'
            }}>
              Revenue Over Time
            </h3>
            <div style={{
              height: '200px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: c.chartBg,
              borderRadius: '0.375rem',
              color: c.textMuted
            }}>
              Chart placeholder
            </div>
          </div>

          <div style={{
            background: c.cardBg,
            border: `1px solid ${c.cardBorder}`,
            borderRadius: '0.5rem',
            padding: '1.5rem',
            backdropFilter: theme === 'dark' ? 'blur(10px)' : 'none',
            boxShadow: theme === 'dark' ? '0 4px 6px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{
              fontSize: '1.125rem',
              fontWeight: '600',
              color: c.text,
              marginBottom: '1rem'
            }}>
              Top Products
            </h3>
            <div style={{
              height: '200px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: c.chartBg,
              borderRadius: '0.375rem',
              color: c.textMuted
            }}>
              No data available
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface MetricCardProps {
  title: string;
  value: string;
  change: string;
  trend: 'up' | 'down' | 'neutral';
  theme: 'dark' | 'light';
}

function MetricCard({ title, value, change, trend, theme }: MetricCardProps) {
  const trendColor = trend === 'up' ? '#059669' : trend === 'down' ? '#dc2626' : '#6b7280';

  const colors = {
    dark: {
      cardBg: 'linear-gradient(135deg, rgba(26, 26, 46, 0.6) 0%, rgba(22, 33, 62, 0.6) 100%)',
      cardBorder: 'rgba(102, 126, 234, 0.2)',
      text: '#ffffff',
      textMuted: '#9ca3af'
    },
    light: {
      cardBg: '#ffffff',
      cardBorder: '#e5e7eb',
      text: '#111827',
      textMuted: '#6b7280'
    }
  };

  const c = colors[theme];

  return (
    <div style={{
      background: c.cardBg,
      border: `1px solid ${c.cardBorder}`,
      borderRadius: '0.5rem',
      padding: '1.5rem',
      backdropFilter: theme === 'dark' ? 'blur(10px)' : 'none',
      boxShadow: theme === 'dark' ? '0 4px 6px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.1)'
    }}>
      <h3 style={{
        fontSize: '0.875rem',
        fontWeight: '500',
        color: c.textMuted,
        marginBottom: '0.5rem'
      }}>
        {title}
      </h3>
      <p style={{
        fontSize: '1.875rem',
        fontWeight: 'bold',
        color: c.text,
        marginBottom: '0.5rem'
      }}>
        {value}
      </p>
      <p style={{
        fontSize: '0.875rem',
        color: trendColor,
        fontWeight: '500'
      }}>
        {change} from previous period
      </p>
    </div>
  );
}

export default CreatorsAnalytics;
